/**
 * Utility functions
 */

/**
 * Debounce — delays execution until after wait ms have elapsed since last call
 * @param {Function} fn
 * @param {number} wait
 * @returns {Function}
 */
export function debounce( fn, wait = 150 ) {
	let timer;
	return ( ...args ) => {
		clearTimeout( timer );
		timer = setTimeout( () => fn.apply( this, args ), wait );
	};
}

/**
 * Throttle — ensures fn is called at most once per interval
 * @param {Function} fn
 * @param {number} interval
 * @returns {Function}
 */
export function throttle( fn, interval = 100 ) {
	let lastCall = 0;
	return ( ...args ) => {
		const now = Date.now();
		if ( now - lastCall >= interval ) {
			lastCall = now;
			fn.apply( this, args );
		}
	};
}

/**
 * Query selector shorthand
 * @param {string} selector
 * @param {Element} [context=document]
 * @returns {Element|null}
 */
export const qs = ( selector, context = document ) =>
	context.querySelector( selector );

/**
 * Query selector all shorthand — returns array not NodeList
 * @param {string} selector
 * @param {Element} [context=document]
 * @returns {Element[]}
 */
export const qsa = ( selector, context = document ) =>
	[ ...context.querySelectorAll( selector ) ];

/**
 * Add class(es) to element(s)
 * @param {Element|Element[]} els
 * @param {...string} classes
 */
export function addClass( els, ...classes ) {
	const targets = Array.isArray( els ) ? els : [ els ];
	targets.forEach( ( el ) => el?.classList.add( ...classes ) );
}

/**
 * Remove class(es) from element(s)
 */
export function removeClass( els, ...classes ) {
	const targets = Array.isArray( els ) ? els : [ els ];
	targets.forEach( ( el ) => el?.classList.remove( ...classes ) );
}

/**
 * Toggle class on element
 */
export function toggleClass( el, className, force ) {
	el?.classList.toggle( className, force );
}

/**
 * Check if element matches selector
 */
export function matches( el, selector ) {
	return el?.matches?.( selector ) ?? false;
}

/**
 * Get closest ancestor matching selector
 */
export function closest( el, selector ) {
	return el?.closest( selector ) ?? null;
}

/**
 * Trap focus within a container (for modals/drawers)
 * @param {Element} container
 * @returns {Function} cleanup function
 */
export function trapFocus( container ) {
	const focusable = container.querySelectorAll(
		'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
	);
	const first = focusable[ 0 ];
	const last  = focusable[ focusable.length - 1 ];

	function handler( e ) {
		if ( e.key !== 'Tab' ) return;
		if ( e.shiftKey ) {
			if ( document.activeElement === first ) {
				e.preventDefault();
				last.focus();
			}
		} else if ( document.activeElement === last ) {
			e.preventDefault();
			first.focus();
		}
	}

	container.addEventListener( 'keydown', handler );
	first?.focus();

	return () => container.removeEventListener( 'keydown', handler );
}
